import type { ExternalEventType } from '@pushly/models/lib/structs/external-events/external-event-type'
import { EVENT_PROPERTY_NAMESPACE } from './event-property-fields'

/** The delimited spans of a Liquid template — an output expression or a tag. */
const LIQUID_SPANS = /\{\{(.*?)\}\}|\{%(.*?)%\}/gs

/**
 * A path rooted at the event, as it appears inside a Liquid span.
 *
 * `event` is the root a condition binds to and the one the send path resolves against, so a path
 * that renders is a path that could equally have been targeted on.
 *
 * The leading boundary keeps `myevent.properties.x` from reading as an event path — a partner is
 * free to name things, and a substring match would report a property nobody referenced.
 */
const EVENT_PATH = /(^|[^A-Za-z0-9_.])(event(?:\.[A-Za-z0-9_]+|\[[^\]]*\])+)/g

/**
 * Every path a template interpolates the triggering event through.
 *
 * Only inside `{{ }}` and `{% %}`, so prose that happens to mention a property name is not read as
 * a reference. Ordinary prose is most of a message body, and a warning raised against it would
 * train the author to ignore the one raised against a real macro.
 *
 * A loop variable is not followed. `{% for leg in event.properties.legs %}{{ leg.gate }}{% endfor %}`
 * yields the list it iterates and nothing else, which is correct: `leg` names a row of the
 * partner's data, and whether that row carries a gate is not something the registry declares.
 */
export function collectReferencedEventPaths(template: string): string[] {
    const found = new Set<string>()

    // Both patterns are global and are shared, so each scan starts them over. A `lastIndex` left
    // where the previous scan stopped would silently skip the front of the next template.
    LIQUID_SPANS.lastIndex = 0
    let span: RegExpExecArray | null
    while ((span = LIQUID_SPANS.exec(template)) !== null) {
        const body = span[1] ?? span[2] ?? ''

        EVENT_PATH.lastIndex = 0
        let reference: RegExpExecArray | null
        while ((reference = EVENT_PATH.exec(body)) !== null) {
            found.add(reference[2])
        }
    }

    return Array.from(found)
}

/** A subscript naming a literal position, which the registry types the property behind either way. */
const LITERAL_SUBSCRIPT = /\[\s*\d+\s*\]/g

/** The first subscript settled only while a template runs, and therefore not settleable here. */
const VARIABLE_SUBSCRIPT = /\[\s*(?!\d+\s*\])[^\]]*\]/

/**
 * A path reduced to what can be judged before anything renders.
 *
 * A subscript taken from a variable only has a value while a template runs, so a path containing
 * one is answerable up to that point and no further — truncating is how "answerable so far" is said
 * without claiming to know the rest. A literal position is dropped instead: the registry types a
 * property, not each entry of it, so `legs[0].gate` and `legs[2].gate` ask the same question of it.
 */
function settleablePath(path: string): string {
    const firstVariableSubscript = path.search(VARIABLE_SUBSCRIPT)
    const answerable = firstVariableSubscript === -1 ? path : path.slice(0, firstVariableSubscript)

    return answerable.replace(LITERAL_SUBSCRIPT, '')
}

/**
 * Whether the event type declares something that could answer this property path.
 *
 * Three ways it can, because a registered key is a path in its own right: the reference names the
 * key exactly, names a parent of it, or drills into it. Only a reference that no declared key
 * touches at any depth is unanswerable, which is the conservative side to be on — a message
 * reported as broken when it is not is a warning the author learns to dismiss.
 */
function isDeclared(propertyPath: string, declaredKeys: string[]): boolean {
    if (!propertyPath) return true

    return declaredKeys.some(
        (key) =>
            key === propertyPath ||
            key.startsWith(`${propertyPath}.`) ||
            propertyPath.startsWith(`${key}.`),
    )
}

/**
 * The event paths a message interpolates that its event type cannot answer.
 *
 * The same comparison the send path makes, made while the author is looking at the message. Left
 * until then, an undeclared property is discovered by the send being abandoned — weeks later, with
 * nobody watching, and with nothing on the journey saying why nothing arrived.
 *
 * Only the property namespace is judged. The rest of what an event carries — its name, its
 * identifier, when it occurred — is the envelope every event has, and the registry declares nothing
 * about it, so a reference to it is not this check's to doubt.
 *
 * An event type that could not be read judges nothing. A registry read that failed is not evidence
 * that a property is missing, and reporting every macro in the message as broken on the strength of
 * a failed request is worse than reporting none.
 */
export function undeclaredEventPropertyPaths(
    referencedPaths: string[],
    eventType: ExternalEventType | undefined,
): string[] {
    if (!eventType) return []

    const declaredKeys = (eventType.properties ?? []).map((property) => property.key)

    return referencedPaths.filter((path) => {
        const settleable = settleablePath(path)
        if (!settleable.startsWith(EVENT_PROPERTY_NAMESPACE)) {
            // Either the envelope, or the whole property bag, neither of which the registry types.
            return false
        }

        return !isDeclared(settleable.slice(EVENT_PROPERTY_NAMESPACE.length), declaredKeys)
    })
}

/**
 * What the author is told about a message naming properties the event type does not declare.
 *
 * A warning rather than a block, and deliberately so. The property map is filled in by discovery:
 * a property is registered the first time an event carries it, so a partner who has not yet sent
 * one has an event type that does not declare it — and the person writing the message may well be
 * writing it *because* that property is about to start arriving. Blocking assumes the registry is
 * complete and the message is wrong, which for a new integration is the less likely half.
 *
 * Both sides are named for the same reason: either the message or the registry can be the one that
 * moves, and the author cannot decide which without seeing both.
 */
export function describeUndeclaredEventProperties(
    undeclaredPaths: string[],
    eventType: ExternalEventType | undefined,
): string | undefined {
    if (!undeclaredPaths.length || !eventType) return undefined

    const declaredCount = eventType.properties?.length ?? 0
    const version = `v${eventType.schema_version}`
    const paths = [...undeclaredPaths].sort().join(', ')

    return (
        `This message uses ${paths}, which "${eventType.event_name}" (${version}) does not declare. ` +
        `It declares ${declaredCount} ${declaredCount === 1 ? 'property' : 'properties'}. ` +
        'Those values will be blank when the notification is sent, unless the event type is updated ' +
        'to carry them.'
    )
}
